import React, { useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from 'react-router';
import { toast } from 'react-toastify'

import ApexChart from '../ApexCharts/ApexChart';
import { getImageSpecs, toBase64 } from "../../utils/common";
import { appendModel } from "../../../features/site/siteSlice";
import { apiCall } from "../../utils/ApiUtils";
import { getStartupProgress, populateUser } from "../../../features/session/sessionSlice";
import { updateSite, uploadLogo } from "../../utils/Organizations";

function StartupChecklist(){
    const dispatch = useDispatch()

    const model = useSelector(state => state.site.model)
    const session = useSelector(state => state.session)
    const [activeListItem, setActiveListItem] = useState(null)

    const logoFileRef = useRef(null)
    const avatarFileRef = useRef(null)

    const history = useHistory()

    const { user } = session

    const onLogoChange = async(e) => {
        const brand = model?.organizations[model?.orgActiveIdx]

        const respLogo = await uploadLogo(e.target.files[0])
        let logoObj = respLogo?.response ? JSON.parse(new TextDecoder().decode(respLogo?.response)) : {}

        let modelCopy = {
            logo: logoObj?.logoUrl ?? null,
            orgForm: {...brand},
            organizations: {
                ...model.organizations
            }
        }
        modelCopy.orgForm = {
            ...brand,
            attr: {
                ...brand.attr,
                logo: logoObj?.logoUrl ?? null,
            }
        }

        modelCopy.organizations[model.orgActiveIdx] = {
            ...model.organizations[model.orgActiveIdx],
            attr: {
                ...model.organizations[model.orgActiveIdx].attr,
                logo: logoObj?.logoUrl ?? null,
            }
        }
        
        await dispatch(appendModel(modelCopy))

        await updateSite(brand.siteId, null, {
            site: modelCopy.orgForm
        })

        await dispatch(getStartupProgress())
    }

    const onAvatarChange = async(e) => {
		e.persist()

		const { filesize } = await getImageSpecs(e.target.files[0])

		if(filesize / 1000 > 100){
			toast.error("File size for avatars must not exceed 100 KB", {containerId: "defaultToastContainer"})
		} else {

			const logo = await toBase64(e.target.files[0])
			
			const { response, error } = await apiCall(`user/${user.userId}`,{
				method: 'PUT',
				body: {
					email: user.email, 
					siteId: user.siteId,
					family_name: user.family_name,
					given_name: user.given_name,
					userId: user.userId,
					avatar: logo
				}
			})
			
			if(!error && !response.errorType){
				toast.success("Avatar successfully uploaded!", {containerId: "defaultToastContainer"})
				await dispatch(populateUser({
					...user,
					avatar: logo
				}))

                await dispatch(getStartupProgress())
			}
		}
	}

    const site = model?.sites[model?.siteActiveIdx]
    const brand = model?.organizations[model?.orgActiveIdx]

    let color = "#0cce6b"

    const socialMediaClaimed = site?.attr?.accounts?.fbPage && 
        site?.attr?.accounts?.gmb &&
        site?.attr?.accounts?.linkedinOrg
    
    const paidAdsClaimed = site?.attr?.accounts?.fb &&
        site?.attr?.accounts?.linkedin &&
        site?.attr?.accounts?.bing

    const radialGraph = {
        series: [session.onboarding.startupStats.progress],
        options: {
            chart: {
                height: 175,
                width: 130,
                type: 'radialBar'
            },
            plotOptions: {
                radialBar: {
                    hollow: {
                        size: '60%',
                    },
                    track: {
                        background: "#e7e7e7",
                        strokeWidth: '97%'
                    },
                    dataLabels: {
                        show: true,
                        name: {
                            show: true,
                            color: '#888',
                            fontSize: '18px'
                        },
                        value: {
                            offsetY: -10,
                            formatter: function(val) {
                                return val + "%";
                            },
                            color,
                            fontSize: '24px',
                            show: true,
                        }
                    }
                },
            },
            fill: {
                colors: [color]
            },
            labels: ['']
        }
    }

    return <>
        <h3>Startup Progress</h3>
        <div className="d-flex justify-content-center">
            <ApexChart type='radialBar' height={220} width={160} {...radialGraph} />
        </div>
        <input ref={logoFileRef} type="file" class="form-control hide" onChange={onLogoChange} />
        <input ref={avatarFileRef} type="file" class="form-control hide" onChange={onAvatarChange} />
        <ul class="list-group startup-list">
            <li class={`list-group-item d-flex justify-content-between align-items-center ${activeListItem === "ga" ? "active" : ""}`} 
                onMouseOut={() => setActiveListItem(null)}
                onMouseOver={() => setActiveListItem("ga")}
                onClick={() => history.push("/admin")}>
                <span>
                    <i className="fab fa-google mr-2"></i> Claim Google Analytics
                </span>
                <i className={`fas fa-check-circle ${site?.attr?.accounts?.ga ? "text-success" : ""}`}></i>
            </li>
            <li className={`list-group-item d-flex justify-content-between align-items-center ${activeListItem === "avatar" ? "active" : ""}`} 
                onMouseOut={() => setActiveListItem(null)}
                onMouseOver={() => setActiveListItem("avatar")}
                onClick={() => avatarFileRef.current.click()}>
                <span>
                    <i className="fas fa-user-circle mr-2"></i> User Avatar
                </span>
                <i className={`fas fa-check-circle ${user?.avatar ? "text-success" : ""}`}></i>
            </li>
            <li className={`list-group-item d-flex justify-content-between align-items-center ${activeListItem === "logo" ? "active" : ""}`} 
                onMouseOut={() => setActiveListItem(null)}
                onMouseOver={() => setActiveListItem("logo")}
                onClick={() => logoFileRef.current.click()}>
                <span>
                    <i className="fas fa-building mr-2"></i> Company Logo
                </span>
                <i className={`fas fa-check-circle ${brand?.attr?.logo ? "text-success" : ""}`}></i>
            </li>
            <li className={`list-group-item d-flex justify-content-between align-items-center ${activeListItem === "social" ? "active" : ""}`} 
                onMouseOut={() => setActiveListItem(null)}
                onMouseOver={() => setActiveListItem("social")}
                onClick={() => history.push("/admin")}>
                <span>
                    <i className="fas fa-hashtag mr-2"></i> Claim Social Media
                </span>
                <i className={`fas fa-check-circle ${socialMediaClaimed ? "text-success" : ""}`}></i>
            </li>
            <li className={`list-group-item d-flex justify-content-between align-items-center ${activeListItem === "paidAds" ? "active" : ""}`} 
                onMouseOut={() => setActiveListItem(null)}
                onMouseOver={() => setActiveListItem("paidAds")}
                onClick={() => history.push("/admin")}>
                <span>
                    <i className="fas fa-ad mr-2"></i> Claim Paid Ads Channels
                </span>
                <i className={`fas fa-check-circle ${paidAdsClaimed ? "text-success" : ""}`}></i>
            </li>
        </ul>
    </>
}

export default StartupChecklist